import CalibrationModal from 'components/modals/CalibrationModal';
import MapDrawingSelectionModal from 'components/modals/MapDrawingSelectionModal';
import {
  DetailedDisciplineByKey,
  disciplineByKey,
} from 'constants/disciplines';
import AppStoreContext from 'contexts/AppStoreContext';
import Immutable, { OrderedMap } from 'immutable';
import * as React from 'react';
import { SheetEntry } from 'records/AppState';
import { MappedSheetQuery } from 'records/MappedSheetQuery';
import { MappedSheetVersion } from 'records/MappedSheetVersion';
import { ModalState } from 'records/ModalState';
import { PageId } from 'records/Page';
import { SheetMap } from 'records/Sheet';
import { SheetVersion } from 'records/SheetVersion';
import { Discipline, DisciplineDesignator } from 'records/Discipline';

interface DisciplineSearch {
  key: string;
  discipline: string;
  size: number;
  description: string;
}

const disciplineByKeyWithUnassigned: DetailedDisciplineByKey = {
  ...disciplineByKey,
  Unassigned: {
    key: '',
    discipline: 'Unassigned',
    description: 'Unassigned',
  },
};

type DisciplineKey = keyof typeof disciplineByKey;

export type MapLayersManagerValue = {
  getCurrentMappedSheetEntries: () => Immutable.Map<PageId, SheetEntry>;
  getMappedSheetsDisciplines: () => Array<DisciplineSearch>;
  launchDrawingSelectionModal: () => void;
  mappedSheetQuery: MappedSheetQuery;
  mappedSheetVersions: Immutable.OrderedMap<PageId, MappedSheetVersion>;
  openPrepareDrawingModal: (sheetVersion: SheetVersion) => void;
  queriedMappedSheets: Immutable.List<PageId>;
  queryMappedSheets: () => void;
  removeMappedSheetVersion: (pageId: string) => void;
  setMappedSheetQuery: (mappedSheetQuery: MappedSheetQuery) => void;
  setVisibleMappedSheetVersions: (
    visibleMappedSheetVersions: Immutable.Set<PageId>
  ) => void;
  sheets: SheetMap;
  visibleMappedSheetVersions: Immutable.Set<PageId>;
};

type MapLayersManagerConsumerProps = {
  children: (mapLayersManager: MapLayersManagerValue) => React.ReactNode;
};

export type WithMapLayersManagerProps = {
  mapLayersManager: MapLayersManagerValue;
};

const MapLayersManagerContext =
  React.createContext<MapLayersManagerValue | null>(null);

export const MapLayersManagerProvider = MapLayersManagerContext.Provider;

export const AppMapLayersManagerProvider: React.FC = ({ children }) => {
  const { actions: appActions, state: appState } =
    React.useContext(AppStoreContext);

  const mapLayersManager: MapLayersManagerValue = {
    getCurrentMappedSheetEntries: () => appState.getCurrentMappedSheetEntries(),
    getMappedSheetsDisciplines: () => {
      const sheetsOnMap = appState
        .getCurrentMappedSheetEntries()
        .map((item) => {
          return item.sheet;
        });

      const disciplines = sheetsOnMap.reduce((acc, sheet) => {
        const sheetVersion = sheet.versions.last(new SheetVersion());

        const sheetDiscipline = sheetVersion.get('sheetDiscipline');
        const count = acc.has(sheetDiscipline.designator)
          ? acc.getIn([sheetDiscipline.designator, 'count'])
          : 0;

        return acc.set(
          sheetDiscipline.designator,
          sheetDiscipline.set('count', count + 1)
        );
      }, OrderedMap<DisciplineDesignator, Discipline>());

      return disciplines
        .map<DisciplineSearch>((v) => {
          return {
            key: v.get('designator'),
            discipline: v.get('description'),
            description: v.get('description'),
            size: v.get('count'),
            item: v,
          };
        })
        .sortBy((o) => o.discipline)
        .valueSeq()
        .toArray();
    },
    launchDrawingSelectionModal: () => {
      const modalState = new ModalState({
        id: 'selectDrawingToMap',
        open: true,
        component: MapDrawingSelectionModal,
      });

      appActions.createModal(modalState);
    },
    mappedSheetQuery: appState.mappedSheetQuery,
    mappedSheetVersions: appState.mappedSheetVersions,
    openPrepareDrawingModal: (sheetVersion) => {
      const modalState = new ModalState({
        id: 'calibrateSheet',
        open: true,
        component: CalibrationModal,
        props: {
          sheetVersion,
        },
      });

      appActions.createModal(modalState);
    },
    queriedMappedSheets: appState.queriedMappedSheets,
    queryMappedSheets: () => {
      appActions.queryMappedSheets();
    },
    removeMappedSheetVersion: (pageId) => {
      appActions.removeMappedSheetVersion(pageId);
    },
    setMappedSheetQuery: (mappedSheetQuery) => {
      appActions.setMappedSheetQuery(mappedSheetQuery);
    },
    setVisibleMappedSheetVersions: (
      visibleMappedSheetVersions: Immutable.Set<PageId>
    ) => {
      appActions.setVisibleMappedSheetVersions(visibleMappedSheetVersions);
    },
    sheets: appState.sheets,
    visibleMappedSheetVersions: appState.visibleMappedSheetVersions,
  };

  return (
    <MapLayersManagerProvider value={mapLayersManager}>
      {children}
    </MapLayersManagerProvider>
  );
};

export const useMapLayersManager = (): MapLayersManagerValue => {
  const mapLayersManager = React.useContext(MapLayersManagerContext);

  if (mapLayersManager === null) {
    throw new Error(
      'useMapLayersManager must be used within a MapLayersManagerProvider'
    );
  }

  return mapLayersManager;
};

const MapLayersManagerConsumer: React.FC<MapLayersManagerConsumerProps> = ({
  children,
}) => {
  return (
    <MapLayersManagerContext.Consumer>
      {(mapLayersManager) => {
        if (mapLayersManager === null) {
          throw new Error(
            'MapLayersManagerConsumer must be used within a MapLayersManagerProvider'
          );
        }
        return children(mapLayersManager);
      }}
    </MapLayersManagerContext.Consumer>
  );
};
export { MapLayersManagerConsumer };

export function withMapLayersManager<P>(
  WrappedComponent: React.ComponentType<P & WithMapLayersManagerProps>
): React.FC<P> {
  const WithMapLayersManagerWrapper: React.FC<P> = (props) => (
    <MapLayersManagerConsumer>
      {(mapLayersManager) => (
        // @ts-ignore - issue with @emotion/react https://github.com/emotion-js/emotion/issues/2169
        <WrappedComponent {...props} mapLayersManager={mapLayersManager} />
      )}
    </MapLayersManagerConsumer>
  );

  WithMapLayersManagerWrapper.displayName = `withMapLayersManager(${
    WrappedComponent.displayName || WrappedComponent.name
  })`;

  return WithMapLayersManagerWrapper;
}
